import type { Locale } from './locale';

/**
 * Parses a user-typed number in either locale's conventions:
 *   "1 234,56" · "1,234.56" · "1234.5" · "4.2k" · "1,5m" · "−300"
 * Returns null when the text is not a number. Grouping spaces (incl. no-break spaces) are ignored.
 */
export function parseNumber(input: string, locale: Locale): number | null {
  let s = input
    .trim()
    .replace(/[\s\u00a0\u202f']/g, '')
    .replace(/[−–]/g, '-');
  if (s === '') return null;

  let multiplier = 1;
  const suffix = /([kKmMкКмМ])$/.exec(s)?.[1];
  if (suffix) {
    multiplier = /[kKкК]/.test(suffix) ? 1_000 : 1_000_000;
    s = s.slice(0, -1);
  }

  const commas = (s.match(/,/g) ?? []).length;
  const dots = (s.match(/\./g) ?? []).length;
  /** Removes `sep` used as a thousands separator; null if it isn't one (e.g. "1..2"). */
  const ungroup = (text: string, sep: ',' | '.'): string | null => {
    const [int = '', ...rest] = text.split(sep === ',' ? /(?=[.])/ : /(?=,)/);
    const grouped = new RegExp(`^-?\\d{1,3}(\\${sep}\\d{3})+$`);
    return grouped.test(int) ? int.split(sep).join('') + rest.join('') : null;
  };
  if (commas > 0 && dots > 0) {
    // Both present: the last one is the decimal separator.
    const decimal = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.';
    const group = decimal === ',' ? '.' : ',';
    if ((s.match(decimal === ',' ? /,/g : /\./g) ?? []).length !== 1) return null;
    const ungrouped = ungroup(s, group);
    if (ungrouped === null) return null;
    s = ungrouped.replace(decimal, '.');
  } else if (commas > 1 || dots > 1) {
    const ungrouped = ungroup(s, commas > 1 ? ',' : '.');
    if (ungrouped === null) return null;
    s = ungrouped;
  } else if (commas === 1) {
    // English: "1,234" groups thousands; otherwise (and always in Bulgarian) comma is decimal.
    const isGrouping = locale === 'en' && /^-?\d{1,3},\d{3}$/.test(s);
    s = isGrouping ? s.replace(',', '') : s.replace(',', '.');
  }

  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return null;
  const value = Number(s) * multiplier;
  return Number.isFinite(value) ? value : null;
}
